import React, { useMemo, useState, forwardRef } from 'react';
import moment from 'moment';
import 'moment/locale/nl';
import { useTranslation } from 'react-i18next';

import { FORUM_COMMENT_ID_PREFIX } from '../../../config/constants';
import useClasses from '../../../hooks/useClasses';
import Avatar from '../../Avatar';
import { PostType } from '../../../types';
import ForumCommentForm, { type FormForumCommentValues } from '../../Forms/Forum/Comment';
import Kebab from '../../Kebab';
import DeleteComment from '../DeleteComment';
import DeletePost from '../DeletePost';
import EditComment from '../EditComment';
import EditPost from '../EditPost';
import InappropriateButton from '../Flagging/Inappropriate';
import LikeButton from '../Flagging/Like';
import ReplyButton from '../ReplyButton';

import './style.scss';

export interface ForumPostProps {
  id: number;
  type: PostType;
  author?: string;
  avatar?: number;
  publicationDate: Date;
  updatedDate: Date;
  messageProcessed?: string | React.ReactElement;
  message?: string;
  mentionId?: number;
  mentionName?: string;
  mentionLink?: string;
  isLiked: boolean;
  likeCount?: number;
  isInappropriate?: boolean;
  isOriginalPoster: boolean;
  isFirstPost?: boolean;
  isMyPost: boolean;
  hasCreatedNickname?: boolean;
  onClickReply?: (parentCommentId?: number, parentAuthorName?: string) => void;
  onEditPost?: (values: FormForumCommentValues) => void;
}

moment.locale('nl');

const ForumPost = forwardRef<HTMLDivElement, ForumPostProps>((props, ref) => {
  const {
    id,
    type,
    author,
    avatar,
    publicationDate,
    updatedDate,
    messageProcessed,
    message,
    mentionId,
    mentionName,
    mentionLink,
    isLiked = false,
    likeCount = 0,
    isInappropriate = false,
    isOriginalPoster = false,
    isFirstPost = false,
    isMyPost = false,
    hasCreatedNickname,
    onClickReply,
    onEditPost,
  } = props;

  const { t } = useTranslation();
  const [isEditing, setEditing] = useState(false);

  const { joinClasses, buildClass } = useClasses('ForumPost', {
    'original-poster': isOriginalPoster && isFirstPost,
  });

  const hasBeenEdited = useMemo(() => (
    updatedDate.getTime() - publicationDate.getTime() > 0
  ), [publicationDate, updatedDate]);

  return (
    <div ref={ref} id={`${FORUM_COMMENT_ID_PREFIX}${id}`} className={buildClass()}>
      <div className={buildClass('header')}>
        <div className={buildClass('header__avatar')}>
          <Avatar avatar={avatar} />
        </div>

        <div className={buildClass('header__details')}>
          <div className={buildClass('header__author-details')}>
            <div className={buildClass('header__name')}>
              {author || t('pages.forum.anonymous')}
            </div>

            {isOriginalPoster && (
              <div className={joinClasses(buildClass('header__original-poster'), 'body--small')}>
                {t('pages.forum.original_poster')}
              </div>
            )}
          </div>

          <div className={buildClass('header__date')}>
            <span className="body--tiny">
              {moment(updatedDate).fromNow()}
            </span>
          </div>
        </div>
      </div>

      {isEditing ? (
        <ForumCommentForm
          className={`mt-4 ${type === 'node' && 'pb-4'}`}
          label={t('pages.forum_comment_edit.label')}
          initialValues={{
            body: message,
            parentCommentId: mentionId,
            parentAuthorName: mentionName,
          }}
          onCancel={() => setEditing(false)}
          onSubmit={(values) => {
            onEditPost(values);
            setEditing(false);
          }}
        />
      ) : (
        <>
          <div className={buildClass('content')}>
            {/* Only render a mention name if a comment has a message (has not been deleted) */}
            {(mentionId && mentionName && message) && (
              <a href={mentionLink}>
                <span>{`@${mentionName}`}</span>
              </a>
            )}

            <div className={joinClasses(buildClass('body'), 'u-word-break')}>
              {messageProcessed || String(t('pages.forum.empty_comment'))}
            </div>

            {hasBeenEdited && (
              <span className={joinClasses(buildClass('content__edited-label'), 'body--regular')}>
                {t('pages.forum_comment_edit.edited_label')}
              </span>
            )}
          </div>

          {message && (
            <div className={buildClass('actions')}>
              <LikeButton
                postId={id}
                postType={type}
                value={isLiked}
                likeCount={likeCount}
              />

              {onClickReply && hasCreatedNickname && (
                <ReplyButton
                  onClick={() => {
                    onClickReply(type === 'comment' ? id : null, type === 'comment' ? author : '');
                  }}
                />
              )}

              <Kebab
                iconVariant={2}
                id={id}
                label={t('pages.forum.menu.button')}
              >
                {isMyPost && hasCreatedNickname && type === 'comment' && (
                  <EditComment onClick={() => setEditing(true)} />
                )}

                {isMyPost && hasCreatedNickname && type === 'node' && (
                  <EditPost onClick={() => setEditing(true)} />
                )}

                {isMyPost && type === 'comment' && (
                  <DeleteComment commentId={id} />
                )}

                {isMyPost && type === 'node' && (
                  <DeletePost postId={id} />
                )}

                {!isMyPost && (
                  <InappropriateButton
                    postId={id}
                    postType={type}
                    value={isInappropriate}
                  />
                )}
              </Kebab>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default ForumPost;
